import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import ArticleImage from '@/components/ArticleImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Plane, Map, BookOpen, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles?published_only=true`);
        setArticles(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  return (
    <Layout fullScreen>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-josias.jpg"
            alt={t("Josias exploring Berlin street art", "Josias erkundet Berliner Street Art")}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 rounded-full mb-8 animate-fadeInUp">
              <Plane className="w-4 h-4" />
              <span className="text-sm font-bold text-slate-900">
                {t('Blog personnel', 'Persönlicher Blog')}
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
              {t('Un billet.', 'Ein Ticket.')}
              <br />
              {t('Une valise.', 'Ein Koffer.')}
              <br />
              <span className="text-yellow-400">{t("Et l'Allemagne.", 'Und Deutschland.')}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-10 animate-fadeInUp delay-200 max-w-2xl">
              {t(
                "Ce blog est né d'un départ, d'un choix un peu fou et d'une expérience vécue en Allemagne. Découvertes, doutes, galères et rencontres : tout est ici, sans filtre.",
                "Dieser Blog entstand aus einer Abreise, einer etwas verrückten Entscheidung und einer Erfahrung in Deutschland. Entdeckungen, Zweifel, Herausforderungen und Begegnungen: alles ist hier, ungefiltert."
              )}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 animate-fadeInUp delay-300">
              <Link to="/blog">
                <Button 
                  size="lg" 
                  className="btn-primary rounded-full px-8 py-6 text-base font-bold"
                  data-testid="hero-cta-blog"
                >
                  {t('Lire le blog', 'Blog lesen')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base font-bold bg-white/10 border-white/30 text-white hover:bg-white hover:text-slate-900"
                  data-testid="hero-cta-about"
                >
                  {t('Qui suis-je ?', 'Wer bin ich?')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative Yellow Blob */}
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-yellow-400 rounded-full blur-3xl opacity-30 animate-float"></div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
              {t('Ce que tu trouveras ici', 'Was du hier findest')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
              {t('Un voyage en toute transparence', 'Eine Reise in voller Transparenz')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <Link to="/experience" className="group">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 card-hover" data-testid="feature-experience">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                  <Map className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                  {t('Mon expérience', 'Meine Erfahrung')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t(
                    "Découvre mon parcours en Allemagne, étape par étape : arrivée, culture, langue, doutes et rencontres.",
                    "Entdecke meine Reise in Deutschland, Schritt für Schritt: Ankunft, Kultur, Sprache, Zweifel und Begegnungen."
                  )}
                </p>
              </div>
            </Link>

            {/* Feature 2 */}
            <Link to="/tips" className="group">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 card-hover" data-testid="feature-tips">
                <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-400 transition-colors">
                  <BookOpen className="w-7 h-7 text-yellow-600 group-hover:text-slate-900 transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                  {t('Conseils pour partir', 'Tipps zum Aufbrechen')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t(
                    "Tout ce que j'aurais aimé savoir avant de partir. Des conseils pratiques pour oser franchir le pas.",
                    "Alles, was ich vor meiner Abreise gerne gewusst hätte. Praktische Tipps, um den Schritt zu wagen."
                  )}
                </p>
              </div>
            </Link>

            {/* Feature 3 */}
            <Link to="/contact" className="group">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 card-hover" data-testid="feature-contact">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500 transition-colors">
                  <MessageCircle className="w-7 h-7 text-red-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                  {t('Échangeons', 'Lass uns austauschen')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t(
                    "Une question ? Envie de partager ton expérience ? Je suis là pour échanger et t'aider.",
                    "Eine Frage? Möchtest du deine Erfahrung teilen? Ich bin hier, um zu helfen."
                  )}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 md:py-32 bg-blue-600 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-400 rounded-full blur-3xl opacity-20"></div>
        
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="text-2xl md:text-4xl font-bold text-white leading-tight mb-8 font-outfit">
            "{t(
              "Partir n'est jamais parfait, mais c'est toujours formateur.",
              "Aufbrechen ist nie perfekt, aber immer lehrreich."
            )}"
          </blockquote>
          <p className="text-blue-200 text-lg">
            — {t('Mon mantra depuis le premier jour', 'Mein Mantra seit dem ersten Tag')}
          </p>
        </div>
      </section>

      {/* Latest Articles */}
      {articles.length > 0 && (
        <section className="py-20 md:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <div>
                <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block">
                  Blog
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-outfit">
                  {t('Derniers articles', 'Neueste Artikel')}
                </h2>
              </div>
              <Link to="/blog" className="mt-4 md:mt-0">
                <Button variant="ghost" className="text-blue-600 hover:text-blue-700" data-testid="view-all-articles">
                  {t('Voir tous les articles', 'Alle Artikel anzeigen')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <Link 
                  key={article.id} 
                  to={`/blog/${article.slug}`}
                  className="group"
                  data-testid={`article-card-${index}`}
                >
                  <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover">
                    <div className="aspect-video overflow-hidden">
                      <ArticleImage
                        src={article.image_url}
                        alt={language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        containerClassName="aspect-video"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                        {article.category === 'experience' ? t('Expérience', 'Erfahrung') :
                         article.category === 'tips' ? t('Conseils', 'Tipps') : 'Blog'}
                      </span>
                      <h3 className="text-lg font-bold text-slate-900 mt-2 mb-3 font-outfit group-hover:text-blue-600 transition-colors">
                        {language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">
                        {language === 'fr' ? article.excerpt_fr : (article.excerpt_de || article.excerpt_fr)}
                      </p>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-outfit">
            {t('Prêt à partir ?', 'Bereit aufzubrechen?')}
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {t(
              "Si tu te poses des questions avant de partir, tu es au bon endroit. Pas de discours parfait, pas de parcours idéalisé : juste du vécu.",
              "Wenn du Fragen hast, bevor du aufbrichst, bist du hier richtig. Keine perfekten Reden, keine idealisierten Wege: nur echte Erfahrungen."
            )}
          </p>
          <Link to="/tips">
            <Button 
              size="lg" 
              className="btn-secondary rounded-full px-10 py-6 text-base font-bold"
              data-testid="cta-tips"
            >
              {t('Découvrir mes conseils', 'Meine Tipps entdecken')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
